import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ChatsCircle, CalendarBlank, LinkSimple, Prohibit, Archive } from "@phosphor-icons/react";
import { ConsultNowPanel } from "@/components/ConsultNowPanel";

const SL = { scheduled: "Terjadwal", in_progress: "Berlangsung", ended: "Berakhir" };
const RS = { open: "Aktif", closed: "Ditutup", archived: "Arsip" };

function AdminActions({ r, reload }) {
  const run = async (path, msg) => {
    try { await api.post(`/consult/rooms/${r.id}/${path}`); toast.success(msg); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`tc-resend-${r.id}`} onClick={() => run("resend-link", "Tautan baru dikirim ke pasien")} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-bold text-teal-700"><LinkSimple size={12} /> Kirim Ulang Link</button>
      <button data-testid={`tc-revoke-${r.id}`} onClick={() => run("revoke-link", "Tautan pasien dicabut")} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-700"><Prohibit size={12} /> Cabut Link</button>
      <button data-testid={`tc-archive-${r.id}`} onClick={() => run("archive", "Room diarsipkan")} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600"><Archive size={12} /> Arsipkan</button>
    </div>
  );
}

export default function ConsultRoomsList({ mode = "staff" }) {
  const [rooms, setRooms] = useState([]);
  const nav = useNavigate();
  const isAdmin = mode === "admin";
  const load = useCallback(() => {
    api.get(isAdmin ? "/consult/rooms" : "/consult/rooms/mine").then((r) => setRooms(r.data.rooms)).catch((e) => toast.error(apiError(e)));
  }, [isAdmin]);
  useEffect(() => { document.title = "Konsultasi Online — GBS"; load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  return (
    <div className="mx-auto max-w-3xl p-4" data-testid={isAdmin ? "consult-admin" : "consult-staff"}>
      <h1 className="mb-1 flex items-center gap-2 font-heading text-xl font-extrabold text-[#0F4C47]"><ChatsCircle size={22} weight="duotone" className="text-teal-600" /> {isAdmin ? "Konsultasi Online" : "Konsultasi Online Saya"}</h1>
      <p className="mb-4 text-xs text-slate-500">{isAdmin ? "Metadata administratif saja. Isi percakapan klinis tidak ditampilkan." : "Ruang konsultasi untuk reservasi Online yang ditugaskan kepada Anda. Sesi dimulai dan diakhiri oleh Anda."}</p>
      {!isAdmin && <ConsultNowPanel />}
      {rooms.length === 0 && <p className="rounded-xl border border-dashed border-teal-200 p-8 text-center text-sm text-slate-400" data-testid="tc-list-empty">Belum ada ruang konsultasi.</p>}
      <div className="space-y-2">
        {rooms.map((r) => (
          <div key={r.id} role="button" tabIndex={0} data-testid={`tc-room-${r.id}`} onClick={() => nav(`/konsultasi/${r.id}`)} className="cursor-pointer rounded-2xl border border-teal-100 bg-white p-3 transition-transform active:scale-[.99]">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold text-slate-700">{r.client_name}</p>
              <div className="flex gap-1">
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700" data-testid={`tc-room-session-${r.id}`}>{SL[r.session_status] || "-"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{RS[r.status] || r.status}</span>
              </div>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{r.service || "-"} · {r.assigned_staff_name || "-"}</p>
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500"><CalendarBlank size={13} /> {r.tanggal_kunjungan || "-"} · {r.jam_kunjungan || "-"}{isAdmin && r.activation_wa_status ? ` · WA link: ${r.activation_wa_status}` : ""}{isAdmin && r.client_token_revoked ? " · link dicabut" : ""}</p>
            {isAdmin && r.status !== "archived" && <AdminActions r={r} reload={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
